import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import {
  addTicketNote,
  createTicket,
  getTicket,
  listTickets,
  deleteTicket,
  reopenTicket,
  transitionTicket,
  type TicketPriority,
  type TicketStatus,
  type TicketSummary,
  type TicketType,
} from '../api/tickets';
import { useAuth } from '../auth/AuthProvider';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toasts';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { Icon } from '../lib/icons';
import { TICKET_BADGE, badgeFor } from '../lib/statusStyles';

const PAGE_SIZE = 25;

/** The order the status filter offers them in. */
const TICKET_STATUSES: TicketStatus[] = ['new', 'assigned', 'in_progress', 'resolved', 'rejected'];

/** The prototype's priority pill. */
const PRIORITY_CLASS: Record<TicketPriority, string> = {
  low: 'prio low',
  med: 'prio med',
  high: 'prio high',
};

/**
 * Which transitions a handler may offer from here.
 *
 * Mirrors the server's table. It is a *convenience*, not a rule: the server
 * refuses an illegal transition with 422 regardless of what this renders, and
 * `apps/api/test/support.test.ts` proves it. Duplicating the table client-side
 * only spares the user a button that would fail.
 */
const NEXT_STATUSES: Record<TicketStatus, TicketStatus[]> = {
  new: ['assigned', 'in_progress', 'resolved', 'rejected'],
  assigned: ['in_progress', 'resolved', 'rejected'],
  in_progress: ['resolved', 'rejected'],
  resolved: [],
  rejected: [],
};

export function SupportPage(): ReactElement {
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  /*
   * The prototype offers Update status, Reopen and Delete from the row as well
   * as from the detail dialog, so a handler working a queue never has to open a
   * ticket to move it on.
   */
  const [statusFor, setStatusFor] = useState<TicketSummary | null>(null);
  const [reopenFor, setReopenFor] = useState<TicketSummary | null>(null);
  const [deleteFor, setDeleteFor] = useState<TicketSummary | null>(null);

  const listQuery = useQuery({
    queryKey: ['tickets', scope, statusFilter],
    queryFn: () =>
      listTickets({
        page: 1,
        pageSize: PAGE_SIZE,
        scope,
        ...(statusFilter === '' ? {} : { status: statusFilter }),
      }),
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  }

  return (
    <>
      {/*
        The prototype's page head: eyebrow, title, a lead that states the
        workflow for a handler or the scope for everyone else, and the primary
        action on the right.
      */}
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_ops')}</div>
          <h1>{t('complaints')}</h1>
          <div className="lead">
            {can('Support.ViewAll') ? t('support_flow_lead') : t('scope_own')}
          </div>
        </div>
        {can('Support.Create') ? (
          <button type="button" className="btn primary" onClick={() => setIsCreating(true)}>
            <Icon name="plus" />
            {t('new_ticket')}
          </button>
        ) : null}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('complaints')}</h2>

          <div className="head-tools">
            <div className="tabs" role="group" aria-label={t('scope_filter')}>
              {(['all', 'mine'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`tab${scope === value ? ' on' : ''}`}
                  aria-pressed={scope === value}
                  onClick={() => setScope(value)}
                >
                  {value === 'all' ? t('scope_all') : t('scope_mine')}
                </button>
              ))}
            </div>

            <label className="sr-only" htmlFor="status-filter">
              {t('status')}
            </label>
            {/*
              `fb-input`, the same class the Visitors filter bar puts on its
              hosting-company select. `head-tools` lays its children out and
              styles none of them, so without this the control fell back to the
              browser's default select — a different height, a different border
              and a different font from every other filter in the product.

              The label stays screen-reader-only: this is a compact toolbar
              beside the tabs and the count, not a filter bar with stacked
              labels, so the visible text would crowd the row it sits in.
            */}
            <select
              id="status-filter"
              className="fb-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
            >
              <option value="">{t('all_statuses')}</option>
              {TICKET_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`st_${status}`)}
                </option>
              ))}
            </select>

            <span className="hint">{listQuery.data?.total ?? 0}</span>
          </div>
        </div>

        <TicketTable
          isLoading={listQuery.isPending}
          error={listQuery.error}
          tickets={listQuery.data?.items ?? []}
          currentUserId={user?.id ?? ''}
          canHandle={can('Support.Handle')}
          canDelete={can('Support.Delete')}
          onOpen={setOpenId}
          onUpdateStatus={setStatusFor}
          onReopen={setReopenFor}
          onDelete={setDeleteFor}
        />
      </div>

      {isCreating ? (
        <CreateTicketDialog
          onClose={() => setIsCreating(false)}
          onCreated={(id) => {
            setIsCreating(false);
            refresh();
            notify(t('ticket_raised', { id }));
            setOpenId(id);
          }}
        />
      ) : null}

      {openId ? (
        <TicketDialog
          id={openId}
          currentUserId={user?.id ?? ''}
          canHandle={can('Support.Handle')}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
        />
      ) : null}

      {/*
        The row's three actions, each reusing the dialog the detail view already
        uses. A second implementation of "update status" is a second place for
        the transition table to drift from the server's.
      */}
      {statusFor ? (
        <StatusDialog
          ticketId={statusFor.id}
          current={statusFor.status}
          onClose={() => setStatusFor(null)}
          onChanged={() => {
            setStatusFor(null);
            refresh();
            notify(t('status_updated'));
          }}
        />
      ) : null}

      {reopenFor ? (
        <ReopenDialog
          ticket={reopenFor}
          onClose={() => setReopenFor(null)}
          onChanged={() => {
            setReopenFor(null);
            refresh();
            notify(t('ticket_reopened'));
          }}
        />
      ) : null}

      {deleteFor ? (
        <DeleteTicketDialog
          ticket={deleteFor}
          onClose={() => setDeleteFor(null)}
          onDeleted={() => {
            setDeleteFor(null);
            refresh();
            notify(t('ticket_deleted'));
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Reopening, as the prototype has it: an explanatory note block and an optional
 * reason.
 *
 * The note explains what reopening actually does — the ticket goes back to
 * Reception and loses its assignee — because that is not obvious from the word
 * and it is not undoable.
 */
function ReopenDialog({
  ticket,
  onClose,
  onChanged,
}: {
  ticket: TicketSummary;
  onClose: () => void;
  onChanged: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      await reopenTicket(ticket.id);
      // The note is a separate write: reopening is the state change, and the
      // reason is a message about it.
      if (note.trim()) await addTicketNote(ticket.id, note.trim());
    },
    onSuccess: onChanged,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={t('reopen_q')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn gold"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('reopen')}
          </button>
        </>
      }
    >
      <div className="note">
        <Icon name="info" />
        <div>{t('reopen_note')}</div>
      </div>

      <div className="field">
        <label htmlFor="reopen-note">
          {t('reopen_why')}
          <span className="opt"> {t('optional')}</span>
        </label>
        <textarea
          id="reopen-note"
          rows={3}
          value={note}
          placeholder={t('note_ph')}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function DeleteTicketDialog({
  ticket,
  onClose,
  onDeleted,
}: {
  ticket: TicketSummary;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const mutation = useMutation({
    mutationFn: () => deleteTicket(ticket.id),
    onSuccess: onDeleted,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={t('delete')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('confirm_del')}
          </button>
        </>
      }
    >
      <p>{t('ticket_delete_q', { id: ticket.id, subject: ticket.subject })}</p>
    </Modal>
  );
}

function TicketTable({
  isLoading,
  error,
  tickets,
  currentUserId,
  canHandle,
  canDelete,
  onOpen,
  onUpdateStatus,
  onReopen,
  onDelete,
}: {
  isLoading: boolean;
  error: unknown;
  tickets: TicketSummary[];
  currentUserId: string;
  canHandle: boolean;
  canDelete: boolean;
  onOpen: (id: string) => void;
  onUpdateStatus: (ticket: TicketSummary) => void;
  onReopen: (ticket: TicketSummary) => void;
  onDelete: (ticket: TicketSummary) => void;
}): ReactElement {
  const { t } = useTranslation();

  // Loading, error and empty are first-class states, not afterthoughts.
  if (isLoading) {
    return (
      <div className="empty" role="status" aria-live="polite">
        {t('loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty" role="alert">
        {error instanceof ApiClientError ? error.message : t('login_network_error')}
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="empty">
        <div className="em-ic">
          <Icon name="chat" />
        </div>
        {t('no_tickets')}
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th scope="col">{t('col_subject')}</th>
            <th scope="col">{t('ticket_type')}</th>
            <th scope="col">{t('comp_prio')}</th>
            <th scope="col">{t('status')}</th>
            <th scope="col">
              <span className="sr-only">{t('actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => {
            // Only the person who raised it may reopen, and only once it is
            // closed out. The server enforces both (REQ-034).
            const canReopen =
              ticket.requester_id === currentUserId &&
              (ticket.status === 'resolved' || ticket.status === 'rejected');

            // A closed ticket has no onward transition, so the control is not
            // offered — the server would refuse it with 422.
            const canAdvance =
              canHandle && ticket.status !== 'resolved' && ticket.status !== 'rejected';

            return (
              <ClickableRow key={ticket.id} onOpen={() => onOpen(ticket.id)}>
                <td>
                  <RowTitle onOpen={() => onOpen(ticket.id)}>{ticket.subject}</RowTitle>
                  <div className="t-sub">
                    {ticket.id} · {ticket.requester_name}
                    <span className="co-tag">{ticket.owner_company_name}</span>
                  </div>
                </td>
                <td>
                  <span className="chip">{t(`tt_${ticket.type}`)}</span>
                </td>
                <td>
                  <span className={PRIORITY_CLASS[ticket.priority]}>
                    {t(`prio_${ticket.priority}`)}
                  </span>
                </td>
                <td>
                  <span className={badgeFor(TICKET_BADGE, ticket.status)}>
                    {t(`st_${ticket.status}`)}
                  </span>
                  {ticket.assignee_name ? (
                    <div className="t-sub">{ticket.assignee_name}</div>
                  ) : null}
                </td>
                <td>
                  <div className="row-actions">
                    {canAdvance ? (
                      <button
                        type="button"
                        className="btn sm primary"
                        onClick={() => onUpdateStatus(ticket)}
                      >
                        {t('update_status')}
                      </button>
                    ) : null}

                    {canReopen ? (
                      <button
                        type="button"
                        className="btn sm gold"
                        onClick={() => onReopen(ticket)}
                      >
                        {t('reopen')}
                      </button>
                    ) : null}

                    {canDelete ? (
                      <button
                        type="button"
                        className="btn sm danger"
                        onClick={() => onDelete(ticket)}
                        aria-label={`${t('delete')} ${ticket.id}`}
                      >
                        <Icon name="trash" />
                      </button>
                    ) : null}

                    {/* The prototype shows a dash rather than an empty cell. */}
                    {!canAdvance && !canReopen && !canDelete ? (
                      <span className="chip">—</span>
                    ) : null}
                  </div>
                </td>
              </ClickableRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CreateTicketDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TicketType>('complaint');
  const [priority, setPriority] = useState<TicketPriority>('med');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () =>
      createTicket({
        subject,
        ...(description.trim() === '' ? {} : { description }),
        type,
        priority,
      }),
    onSuccess: (ticket) => onCreated(ticket.id),
    onError: (error) => {
      if (error instanceof ApiClientError) {
        // The server returns every offending field at once (§8.6.3), so all of
        // them are shown — not just the first.
        setFieldErrors(error.fieldErrors);
        notify(error.message, 'bad');
      }
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <Modal
      title={t('new_ticket')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="create-ticket"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('submit')}
          </button>
        </>
      }
    >
      <form id="create-ticket" onSubmit={submit} noValidate>
        {/* Field order, placeholders and labels follow the prototype's own
            new-ticket dialog, so the screen reads the same as it always did. */}
        <div className="field">
          <label htmlFor="subject">{t('col_subject')}</label>
          <input
            id="subject"
            placeholder={t('subject_placeholder')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={fieldErrors.subject ? 'bad' : ''}
            aria-invalid={Boolean(fieldErrors.subject)}
            aria-describedby={fieldErrors.subject ? 'subject-error' : undefined}
            required
          />
          {fieldErrors.subject ? (
            <div id="subject-error" className="field-error" role="alert">
              {fieldErrors.subject}
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="description">
            {t('col_description')} <span className="opt">{t('optional')}</span>
          </label>
          <textarea
            id="description"
            rows={3}
            placeholder={t('description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="type">{t('col_type')}</label>
          <select id="type" value={type} onChange={(e) => setType(e.target.value as TicketType)}>
            {(['complaint', 'suggestion', 'question', 'other'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`tt_${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="priority">{t('col_priority')}</label>
          <select
            id="priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            {(['low', 'med', 'high'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`pr_${value}`)}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Modal>
  );
}

function TicketDialog({
  id,
  currentUserId,
  canHandle,
  onClose,
  onChanged,
}: {
  id: string;
  currentUserId: string;
  canHandle: boolean;
  onClose: () => void;
  onChanged: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [note, setNote] = useState('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  const ticketQuery = useQuery({ queryKey: ['ticket', id], queryFn: () => getTicket(id) });
  const ticket = ticketQuery.data;

  function afterChange(message: string) {
    return () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      onChanged();
      notify(message);
    };
  }

  function onFailure(error: unknown): void {
    if (error instanceof ApiClientError) notify(error.message, 'bad');
  }

  const reopen = useMutation({
    mutationFn: () => reopenTicket(id),
    onSuccess: afterChange(t('ticket_reopened')),
    onError: onFailure,
  });

  const addNote = useMutation({
    mutationFn: () => addTicketNote(id, note),
    onSuccess: () => {
      setNote('');
      afterChange(t('note_added'))();
    },
    onError: onFailure,
  });

  const isRequester = ticket?.requester_id === currentUserId;
  const isClosed = ticket?.status === 'resolved' || ticket?.status === 'rejected';
  const canUpdateStatus = canHandle && ticket && NEXT_STATUSES[ticket.status].length > 0;

  return (
    <>
      {/*
        Laid out as the prototype's detail dialog: the subject as the title, a
        definition list of facts, then the description, notes and history. Wide,
        because a two-column `dl` needs the room.
      */}
      <Modal
        wide
        title={ticket ? ticket.subject : t('loading')}
        onClose={onClose}
        footer={
          ticket ? (
            <>
              <button type="button" className="btn ghost" onClick={onClose}>
                {t('close')}
              </button>

              {/*
                One "Update status" action opening a dialog, rather than a row of
                transition buttons. That is what the prototype does, and it is
                also what lets a handler attach a note to the change.
              */}
              {canUpdateStatus ? (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setIsChangingStatus(true)}
                >
                  {t('update_status')}
                </button>
              ) : null}

              {/* Reopening is the requester's right, not a capability (REQ-033). */}
              {isRequester && isClosed ? (
                <button
                  type="button"
                  className="btn gold"
                  onClick={() => reopen.mutate()}
                  disabled={reopen.isPending}
                >
                  {t('reopen')}
                </button>
              ) : null}
            </>
          ) : null
        }
      >
        {ticketQuery.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : null}

        {ticketQuery.error ? (
          <div className="empty" role="alert">
            {ticketQuery.error instanceof ApiClientError
              ? ticketQuery.error.message
              : t('login_network_error')}
          </div>
        ) : null}

        {ticket ? (
          <>
            <dl className="dl">
              <dt>{t('col_id')}</dt>
              <dd>{ticket.id}</dd>

              <dt>{t('col_status')}</dt>
              <dd>
                <span className={badgeFor(TICKET_BADGE, ticket.status)}>
                  {t(`st_${ticket.status}`)}
                </span>
              </dd>

              <dt>{t('col_type')}</dt>
              <dd>
                <span className="chip">{t(`tt_${ticket.type}`)}</span>
              </dd>

              <dt>{t('col_priority')}</dt>
              <dd>
                <span className={`prio ${ticket.priority}`}>{t(`pr_${ticket.priority}`)}</span>
              </dd>

              <dt>{t('col_requester')}</dt>
              <dd>
                {ticket.requester_name}
                <span className="co-tag">{ticket.owner_company_name}</span>
              </dd>

              {ticket.assignee_name ? (
                <>
                  <dt>{t('col_assignee')}</dt>
                  <dd>{ticket.assignee_name}</dd>
                </>
              ) : null}

              {ticket.reopens > 0 ? (
                <>
                  <dt>{t('reopened_n')}</dt>
                  <dd>{ticket.reopens}×</dd>
                </>
              ) : null}
            </dl>

            <div className="d-sec">{t('col_description')}</div>
            <div className="d-body">{ticket.description}</div>

            {ticket.attachments.length > 0 ? (
              <>
                <div className="d-sec">{t('attachments')}</div>
                <ul className="tl">
                  {ticket.attachments.map((file) => (
                    <li key={file.id}>
                      <Icon name="paperclip" />
                      <div>
                        {file.name}
                        {/* BR-046 — a file is not downloadable until it is scanned. */}
                        {file.scan_status === 'clean' ? null : (
                          <span className="chip">{t(`scan_${file.scan_status}`)}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {ticket.notes.length > 0 ? (
              <>
                <div className="d-sec">{t('notes')}</div>
                <ul className="tl">
                  {ticket.notes.map((entry) => (
                    <li key={entry.id}>
                      <div>
                        <strong>{entry.author_name}</strong>
                        <div className="note">{entry.text}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <div className="d-sec">{t('history')}</div>
            <ul className="tl">
              {ticket.history.map((entry) => (
                <li key={entry.id}>
                  <div>
                    <strong>{entry.author_name}</strong>{' '}
                    {entry.from_status
                      ? t('moved_to', { status: t(`st_${entry.to_status}`) })
                      : t('raised')}
                    {entry.text ? <div className="note">{entry.text}</div> : null}
                  </div>
                </li>
              ))}
            </ul>

            <div className="field">
              <label htmlFor="note">
                {t('add_note')} <span className="opt">{t('optional')}</span>
              </label>
              <textarea
                id="note"
                rows={2}
                placeholder={t('note_placeholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                type="button"
                className="btn sm"
                onClick={() => addNote.mutate()}
                disabled={note.trim() === '' || addNote.isPending}
              >
                {t('add_note')}
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {isChangingStatus && ticket ? (
        <StatusDialog
          ticketId={ticket.id}
          current={ticket.status}
          onClose={() => setIsChangingStatus(false)}
          onChanged={() => {
            setIsChangingStatus(false);
            afterChange(t('ticket_updated'))();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The prototype's status dialog: pick the new status, optionally say why.
 *
 * The optional note is the reason this is a dialog rather than a row of
 * buttons — a transition with an explanation attached is far more use to the
 * next person reading the ticket than a bare status change.
 */
function StatusDialog({
  ticketId,
  current,
  onClose,
  onChanged,
}: {
  ticketId: string;
  current: TicketStatus;
  onClose: () => void;
  onChanged: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const allowed = NEXT_STATUSES[current];
  const [status, setStatus] = useState<TicketStatus>(allowed[0] ?? current);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      transitionTicket(ticketId, {
        to: status as Exclude<TicketStatus, 'new'>,
        ...(note.trim() === '' ? {} : { note }),
      }),
    onSuccess: onChanged,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={`${t('update_status')} · ${ticketId}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('save')}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="new-status">{t('new_status')}</label>
        <select
          id="new-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as TicketStatus)}
        >
          {allowed.map((value) => (
            <option key={value} value={value}>
              {t(`st_${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="status-note">
          {t('reply_note')} <span className="opt">{t('optional')}</span>
        </label>
        <textarea
          id="status-note"
          rows={3}
          placeholder={t('note_placeholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}
